import { useState, useEffect } from "react";
import {
  Typography,
  Card,
  Progress,
  Button,
  Space,
  Tag,
  Alert,
  Skeleton,
  Row,
  Col,
  App,
  Grid,
  Table,
  Switch,
  Statistic,
  Select,
  InputNumber,
  List,
} from "antd";
import { MaterialIcon } from "@/components/MaterialIcon";
import { useAuthStore } from "@/store/authStore";
import { usePlansConfigV3 } from "@/hooks/usePlansConfigV3";
import { collection, doc, getDocs, onSnapshot, Timestamp } from "firebase/firestore";
import { db } from "@/config/firebase";
import {
  createCustomerPortalSession,
  createCheckoutSessionV3,
  upgradeViewV3,
  downgradeViewV3,
  cancelViewV3,
  resumeViewV3,
  toggleAutoPayOverageV3,
  purchaseStorageAddonV3,
  cancelStorageAddonV3,
  purchaseCreditsAddonV3,
  formatStorage,
} from "@/services/stripeService";
import { useNavigate } from "react-router-dom";
import AppLayout from "@/components/AppLayout";
import { useStorageQuota } from "@/hooks/useStorageQuota";
import { UserPlanV3, ViewLicense, PlanTierV3, View } from "@/types";

const { Title, Text } = Typography;

const TIER_COLORS: Record<string, string> = {
  free: "default",
  hobby: "blue",
  plus: "purple",
  pro: "gold",
  site: "volcano",
  beta: "cyan",
};

const TIER_ORDER: Record<string, number> = { free: 0, hobby: 1, plus: 2, pro: 3, site: 4 };

const PAID_TIERS: PlanTierV3[] = ["hobby", "plus", "pro", "site"];

function formatCredits(n: number): string {
  if (n >= 1000) return `${(n / 1000).toFixed(n % 1000 === 0 ? 0 : 1)}k`;
  return String(n);
}

function daysUntil(ts: Timestamp | undefined): number | null {
  if (!ts) return null;
  const diff = ts.toDate().getTime() - Date.now();
  return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
}

interface ViewRow {
  viewId: string;
  projectId: string;
  projectName: string;
  viewName: string;
  storageBytes: number;
  photoCount: number;
  license?: ViewLicense;
}

export default function AccountPage() {
  const { user: authUser } = useAuthStore();
  const { plansConfig, loading: plansLoading } = usePlansConfigV3();
  const { message, modal } = App.useApp();
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.md;

  const [userData, setUserData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [loadingAction, setLoadingAction] = useState<string | null>(null);
  const [views, setViews] = useState<ViewRow[]>([]);
  const [viewsLoading, setViewsLoading] = useState(true);
  const [licenses, setLicenses] = useState<Map<string, ViewLicense>>(new Map());
  // True once the viewLicenses subscription has fired at least once
  const [licensesReady, setLicensesReady] = useState(false);
  const [addOnCreditsPacks, setAddOnCreditsPacks] = useState(1);
  // Safety valve for the loading gate: never hold the skeleton longer than 4s
  const [forceReveal, setForceReveal] = useState(false);
  useEffect(() => {
    const t = window.setTimeout(() => setForceReveal(true), 4000);
    return () => window.clearTimeout(t);
  }, []);

  useEffect(() => {
    if (!authUser?.uid) return;
    const unsubscribe = onSnapshot(
      doc(db, "users", authUser.uid),
      (docSnap) => {
        if (docSnap.exists()) setUserData(docSnap.data());
        setLoading(false);
      },
      () => setLoading(false)
    );
    return () => unsubscribe();
  }, [authUser?.uid]);

  useEffect(() => {
    if (!authUser?.uid) return;
    const unsubscribe = onSnapshot(
      collection(db, `users/${authUser.uid}/viewLicenses`),
      (snapshot) => {
        const map = new Map<string, ViewLicense>();
        snapshot.docs.forEach((d) => map.set(d.id, d.data() as ViewLicense));
        setLicenses(map);
        setLicensesReady(true);
      },
      () => setLicensesReady(true)
    );
    return () => unsubscribe();
  }, [authUser?.uid]);

  useEffect(() => {
    if (!authUser?.uid) return;
    let cancelled = false;

    const loadViews = async () => {
      const projectsSnap = await getDocs(collection(db, `users/${authUser.uid}/projects`));
      const allViews: ViewRow[] = [];

      for (const projectDoc of projectsSnap.docs) {
        const projectName = projectDoc.data().name || projectDoc.id;
        const viewsSnap = await getDocs(
          collection(db, `users/${authUser.uid}/projects/${projectDoc.id}/views`)
        );
        for (const viewDoc of viewsSnap.docs) {
          const data = viewDoc.data() as View;
          allViews.push({
            viewId: viewDoc.id,
            projectId: projectDoc.id,
            projectName,
            viewName: data.name,
            storageBytes: data.stats?.storageBytes ?? (data.stats?.storageMB ?? 0) * 1024 * 1024,
            photoCount: data.stats?.photoCount ?? 0,
          });
        }
      }

      if (!cancelled) {
        setViews(allViews);
        setViewsLoading(false);
      }
    };

    loadViews().catch(() => { if (!cancelled) setViewsLoading(false); });
    return () => { cancelled = true; };
  }, [authUser?.uid, userData]);

  const navigate = useNavigate();
  const planV3: UserPlanV3 | undefined = userData?.planV3;
  const isBeta = planV3?.tier === "beta";

  const viewRows: (ViewRow & { license?: ViewLicense })[] = views.map((v) => ({
    ...v,
    license: licenses.get(v.viewId),
  }));

  const activeViewCount = isBeta
    ? views.length
    : Array.from(licenses.values()).filter(
        (l) => l.status === "active" || l.status === "cancelling"
      ).length;

  const storageQuota = useStorageQuota();

  const handleManagePayment = async () => {
    setLoadingAction("portal");
    try {
      const url = await createCustomerPortalSession();
      window.location.href = url;
    } catch {
      message.error("Failed to open payment portal");
      setLoadingAction(null);
    }
  };

  const handleSubscribeView = async (viewId: string, projectId: string, tier: PlanTierV3) => {
    if (tier === "free" || tier === "beta") return;
    setLoadingAction(`subscribe-${viewId}`);
    try {
      const url = await createCheckoutSessionV3({ tier, viewId, projectId });
      window.location.href = url;
    } catch {
      message.error("Failed to start checkout");
      setLoadingAction(null);
    }
  };

  const handleUpgradeView = async (viewId: string, targetTier: "hobby" | "plus" | "pro") => {
    setLoadingAction(`upgrade-${viewId}`);
    try {
      const result = await upgradeViewV3(viewId, targetTier);
      if (result.success) {
        message.success(`Upgraded to ${plansConfig?.plans[targetTier]?.name}${result.bonusCreditsGranted > 0 ? ` (+${formatCredits(result.bonusCreditsGranted)} credits)` : ""}`);
      }
    } catch {
      message.error("Failed to upgrade view");
    } finally {
      setLoadingAction(null);
    }
  };

  const handleDowngradeView = async (viewId: string, targetTier: "hobby" | "plus" | "pro") => {
    setLoadingAction(`downgrade-${viewId}`);
    try {
      const result = await downgradeViewV3(viewId, targetTier);
      if (result.success) {
        message.success(`Downgrade to ${plansConfig?.plans[targetTier]?.name} scheduled${result.effectiveAt ? ` for ${new Date(result.effectiveAt).toLocaleDateString()}` : ""}`);
      }
    } catch {
      message.error("Failed to downgrade view");
    } finally {
      setLoadingAction(null);
    }
  };

  const handleCancelView = async (viewId: string, viewName: string) => {
    modal.confirm({
      title: `Cancel subscription for "${viewName}"?`,
      content: "The view stays active until the end of the billing period, then enters a 30-day read-only grace period before data is deleted.",
      okText: "Cancel Subscription",
      okButtonProps: { danger: true },
      cancelText: "Keep",
      onOk: async () => {
        setLoadingAction(`cancel-${viewId}`);
        try {
          const result = await cancelViewV3(viewId);
          if (result.success) {
            message.success(`Subscription will end${result.effectiveAt ? ` on ${new Date(result.effectiveAt).toLocaleDateString()}` : " at period end"}`);
          }
        } catch {
          message.error("Failed to cancel subscription");
        } finally {
          setLoadingAction(null);
        }
      },
    });
  };

  const handleResumeView = async (viewId: string) => {
    setLoadingAction(`resume-${viewId}`);
    try {
      const result = await resumeViewV3(viewId);
      if (result.success) message.success("Subscription resumed!");
    } catch {
      message.error("Failed to resume subscription");
    } finally {
      setLoadingAction(null);
    }
  };

  const handleToggleAutoPay = async (checked: boolean) => {
    setLoadingAction("autopay");
    try {
      await toggleAutoPayOverageV3(checked);
      message.success(checked ? "Auto-pay enabled" : "Auto-pay disabled");
    } catch {
      message.error("Failed to update auto-pay setting");
    } finally {
      setLoadingAction(null);
    }
  };

  const handlePurchaseStorage = async (viewId: string, quantity: number) => {
    setLoadingAction(`buy-storage-${viewId}`);
    try {
      const url = await purchaseStorageAddonV3(viewId, quantity);
      window.location.href = url;
    } catch {
      message.error("Failed to start checkout");
      setLoadingAction(null);
    }
  };

  const handleCancelStorageAddon = async (viewId: string) => {
    setLoadingAction(`cancel-storage-${viewId}`);
    try {
      const result = await cancelStorageAddonV3(viewId);
      if (result.success) message.success("Storage add-on will be cancelled at period end");
    } catch {
      message.error("Failed to cancel storage add-on");
    } finally {
      setLoadingAction(null);
    }
  };

  const handlePurchaseCredits = async () => {
    setLoadingAction("buy-credits");
    try {
      const url = await purchaseCreditsAddonV3(addOnCreditsPacks);
      window.location.href = url;
    } catch {
      message.error("Failed to start checkout");
      setLoadingAction(null);
    }
  };

  // Single reveal: user doc, plans config, views and licenses all load behind
  // ONE skeleton shaped like the real page — no full-page spinner followed by
  // a second spinner inside the Views card. The 4s valve only covers the
  // added licensesReady signal, never the primary loading flags.
  const pageReady =
    !loading && !plansLoading && !viewsLoading && (licensesReady || forceReveal);

  if (!pageReady) {
    return (
      <AppLayout>
        <div style={{ maxWidth: 900, margin: "0 auto", padding: "24px 16px" }}>
          <div style={{ marginBottom: 24 }}>
            <Title level={2} style={{ margin: 0 }}>Plans</Title>
            <Text type="secondary">Manage your views, credits, and billing</Text>
          </div>
          <Row gutter={[16, 16]}>
            <Col xs={24}>
              <Card title="Views">
                <Skeleton active title={false} paragraph={{ rows: 4 }} />
              </Card>
            </Col>
            <Col xs={24}>
              <Card title="Rendering Credits">
                <Skeleton active title={false} paragraph={{ rows: 3 }} />
              </Card>
            </Col>
          </Row>
        </div>
      </AppLayout>
    );
  }

  // In the per-view model the tier lives on each view's license, so a missing
  // planV3 says nothing about what the account has: every view is born with a
  // free license while planV3 (credits, Stripe customer, beta flag) is written
  // only when something needs it. Gating this page on planV3 hid the whole view
  // table from every account without one.
  //
  // Nothing to show only when there is genuinely nothing: no views at all.
  if (views.length === 0 && licenses.size === 0) {
    return (
      <AppLayout>
        <div style={{ padding: 24, maxWidth: 600, margin: "0 auto" }}>
          <Title level={2}>Plans</Title>
          <Card>
            <Text>No views yet. Create a project and add a view to get started.</Text>
            <div style={{ marginTop: 16 }}>
              <Button type="primary" onClick={() => navigate("/projects")}>
                Go to projects
              </Button>
            </div>
          </Card>
        </div>
      </AppLayout>
    );
  }

  const viewColumns = [
    {
      title: "View",
      key: "name",
      render: (_: any, record: ViewRow) => (
        <div>
          <Text strong>{record.viewName}</Text>
          <br />
          <Text type="secondary" style={{ fontSize: 12 }}>{record.projectName}</Text>
        </div>
      ),
    },
    {
      title: "Tier",
      key: "tier",
      width: 130,
      render: (_: any, record: ViewRow) => {
        const tier = record.license?.tier;
        if (!tier) {
          // The license is created by the onViewCreate trigger, a moment after the
          // view itself. A missing license means it is still in flight.
          if (isBeta) return <Tag color={TIER_COLORS.beta}>Beta</Tag>;
          return <Tag color={TIER_COLORS.free}>Free</Tag>;
        }
        return (
          <Tag color={TIER_COLORS[tier]}>
            {plansConfig?.plans[tier]?.name || tier}
          </Tag>
        );
      },
    },
    {
      title: "Storage",
      key: "storage",
      width: 180,
      render: (_: any, record: ViewRow) => {
        if (!record.license) return <Text type="secondary">{formatStorage(record.storageBytes)}</Text>;
        if (isBeta) {
          return (
            <div>
              <Text style={{ fontSize: 11 }}>{formatStorage(record.storageBytes)}</Text>
              <br />
              <Text type="secondary" style={{ fontSize: 10 }}>{record.photoCount.toLocaleString()} photos</Text>
            </div>
          );
        }
        const tierConfig = plansConfig?.plans[record.license.tier];
        const quota = (tierConfig?.perView?.storageBytes || 0) + (record.license.addOnStorageBytes || 0);
        const pct = quota > 0 ? Math.min(Math.round((record.storageBytes / quota) * 100), 100) : 0;
        return (
          <div style={{ minWidth: 120 }}>
            <Progress percent={pct} size="small" status={pct >= 90 ? "exception" : "normal"} />
            <Text style={{ fontSize: 11 }}>{formatStorage(record.storageBytes)} / {formatStorage(quota)}</Text>
            {record.license.addOnStorageBytes > 0 && (
              <div><Text type="secondary" style={{ fontSize: 10 }}>+{formatStorage(record.license.addOnStorageBytes)} add-on</Text></div>
            )}
          </div>
        );
      },
    },
    {
      title: "Status",
      key: "status",
      width: 140,
      render: (_: any, record: ViewRow) => {
        if (!record.license) {
          if (isBeta) return <Tag color="green">Active</Tag>;
          return <Tag>Unlicensed</Tag>;
        }
        if (record.license.status === "grace_period") {
          const days = daysUntil(record.license.graceExpiresAt);
          return <Tag color="orange">Grace — {days}d left</Tag>;
        }
        if (record.license.status === "cancelling") {
          const days = daysUntil(record.license.currentPeriodEnd);
          return <Tag color="orange">Cancelling{days !== null ? ` — ${days}d` : ""}</Tag>;
        }
        if (record.license.status === "expired") return <Tag color="red">Expired</Tag>;
        return <Tag color="green">Active</Tag>;
      },
    },
    {
      title: "",
      key: "actions",
      width: 200,
      render: (_: any, record: ViewRow) => {
        const license = record.license;

        if (isBeta) return null;

        if (!license || license.status === "expired" || license.status === "grace_period") {
          const availableTiers = PAID_TIERS;
          return (
            <Select
              placeholder="Subscribe..."
              size="small"
              style={{ width: 140 }}
              loading={loadingAction === `subscribe-${record.viewId}`}
              onSelect={(tier: PlanTierV3) => handleSubscribeView(record.viewId, record.projectId, tier)}
            >
              {availableTiers.map((t) => (
                <Select.Option key={t} value={t}>
                  {plansConfig?.plans[t]?.name || t} — €{((plansConfig?.plans[t]?.pricing.eurCentsPerViewMonth || 0) / 100).toFixed(0)}/mo
                </Select.Option>
              ))}
            </Select>
          );
        }

        if (license.status === "cancelling") {
          return (
            <Button
              size="small"
              onClick={() => handleResumeView(record.viewId)}
              loading={loadingAction === `resume-${record.viewId}`}
            >
              Resume
            </Button>
          );
        }

        const currentOrder = TIER_ORDER[license.tier] || 0;
        const upgrades = PAID_TIERS.filter((t) => TIER_ORDER[t] > currentOrder);
        const downgrades = PAID_TIERS.filter((t) => TIER_ORDER[t] < currentOrder);

        return (
          <Space size={4} wrap>
            {upgrades.length > 0 && (
              <Select
                placeholder="Upgrade"
                size="small"
                style={{ width: 100 }}
                loading={loadingAction === `upgrade-${record.viewId}`}
                onSelect={(t: "hobby" | "plus" | "pro") => handleUpgradeView(record.viewId, t)}
              >
                {upgrades.map((t) => (
                  <Select.Option key={t} value={t}>
                    {plansConfig?.plans[t]?.name || t}
                  </Select.Option>
                ))}
              </Select>
            )}
            {downgrades.length > 0 && (
              <Select
                placeholder="Downgrade"
                size="small"
                style={{ width: 108 }}
                loading={loadingAction === `downgrade-${record.viewId}`}
                onSelect={(t: "hobby" | "plus" | "pro") => handleDowngradeView(record.viewId, t)}
              >
                {downgrades.map((t) => (
                  <Select.Option key={t} value={t}>
                    {plansConfig?.plans[t]?.name || t}
                  </Select.Option>
                ))}
              </Select>
            )}
            {!license.addOnStorageStripeSubscriptionId ? (
              <Button
                size="small"
                type="text"
                icon={<MaterialIcon name="add_circle_outline" size={14} />}
                onClick={() => handlePurchaseStorage(record.viewId, 1)}
                loading={loadingAction === `buy-storage-${record.viewId}`}
                title="Add 50 GB storage"
              />
            ) : (
              <Button
                size="small"
                type="text"
                danger
                icon={<MaterialIcon name="remove_circle_outline" size={14} />}
                onClick={() => handleCancelStorageAddon(record.viewId)}
                loading={loadingAction === `cancel-storage-${record.viewId}`}
                title="Cancel storage add-on"
              />
            )}
            <Button
              size="small"
              type="text"
              danger
              icon={<MaterialIcon name="cancel" size={14} />}
              onClick={() => handleCancelView(record.viewId, record.viewName)}
              loading={loadingAction === `cancel-${record.viewId}`}
              title="Cancel subscription"
            />
          </Space>
        );
      },
    },
  ];

  // planV3 is written only when something needs it (credits, Stripe, beta),
  // so an account can hold licensed views and no planV3 at all.
  const rawCredits = planV3?.credits;
  const credits = {
    balance: rawCredits?.balance ?? 0,
    bonusGranted: rawCredits?.bonusGranted ?? 0,
    purchased: rawCredits?.purchased ?? 0,
    consumed: rawCredits?.consumed ?? 0,
  };

  return (
    <AppLayout>
      <div style={{ maxWidth: 900, margin: "0 auto", padding: "24px 16px" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12, marginBottom: 24 }}>
          <div>
            <Title level={2} style={{ margin: 0 }}>Plans</Title>
            <Text type="secondary">Manage your views, credits, and billing</Text>
          </div>
          <Space wrap>
            {!isBeta && (
              <Button
                href="https://www.masterlapse.com/pricing/"
                target="_blank"
                icon={<MaterialIcon name="open_in_new" size={16} />}
              >
                View Plans
              </Button>
            )}
            {planV3?.stripeCustomerId && (
              <Button
                icon={<MaterialIcon name="credit_card" size={16} />}
                onClick={handleManagePayment}
                loading={loadingAction === "portal"}
              >
                Billing
              </Button>
            )}
          </Space>
        </div>

        {isBeta && (
          <Alert
            type="success"
            message="Beta — free access"
            description={
              <div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: "4px 24px", marginBottom: 8 }}>
                  <span><MaterialIcon name="cloud" size={16} style={{ verticalAlign: "text-bottom" }} /> <strong>Storage:</strong> {formatStorage(storageQuota.usedBytes)} / {formatStorage(storageQuota.totalBytes)}</span>
                  <span><MaterialIcon name="movie" size={16} style={{ verticalAlign: "text-bottom" }} /> <strong>Credits:</strong> Unlimited ({credits.consumed.toLocaleString()} consumed)</span>
                </div>
                <div>As a beta tester, all features are unlocked so you can fully explore the system. Your storage cap is shared across all your views at the account level, rather than per individual view. This works differently from <a href="https://masterlapse.com/pricing/" target="_blank" rel="noopener noreferrer">regular plans</a>, where features and storage depend on the tier you choose for each view.</div>
                <div style={{ marginTop: 8 }}><strong>Need more storage?</strong> Plans and credits cannot be purchased during beta. Contact us at <a href="mailto:info@masterlapse.com">info@masterlapse.com</a> or <a href="https://wa.me/393468118295" target="_blank" rel="noopener noreferrer">WhatsApp +39 346 811 8295</a>.</div>
              </div>
            }
            style={{ marginBottom: 16 }}
            showIcon
          />
        )}

        <Row gutter={[16, 16]}>
          {/* Views Table */}
          <Col xs={24}>
            <Card
              title={
                <Space>
                  <span>Views</span>
                  <Tag>{activeViewCount} active</Tag>
                </Space>
              }
            >
              {viewsLoading ? (
                <Skeleton active title={false} paragraph={{ rows: 4 }} />
              ) : views.length === 0 ? (
                <Text type="secondary">No views yet. Create a project and add views to get started.</Text>
              ) : isMobile ? (
                <List
                  dataSource={viewRows}
                  renderItem={(record) => {
                    const tier = record.license?.tier;
                    const tierLabel = isBeta ? "Beta" : (tier ? (plansConfig?.plans[tier]?.name || tier) : "Free");
                    const tierColor = isBeta ? TIER_COLORS.beta : TIER_COLORS[tier || "free"];
                    const status = isBeta ? null : (
                      !record.license ? <Tag>Unlicensed</Tag> :
                      record.license.status === "grace_period" ? <Tag color="orange">Grace — {daysUntil(record.license.graceExpiresAt)}d</Tag> :
                      record.license.status === "cancelling" ? <Tag color="orange">Cancelling</Tag> :
                      record.license.status === "expired" ? <Tag color="red">Expired</Tag> :
                      <Tag color="green">Active</Tag>
                    );
                    return (
                      <List.Item style={{ padding: "8px 0" }}>
                        <div style={{ width: "100%" }}>
                          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            <div>
                              <Text strong>{record.viewName}</Text>
                              <br />
                              <Text type="secondary" style={{ fontSize: 12 }}>{record.projectName}</Text>
                            </div>
                            <Space size={4}>
                              <Tag color={tierColor}>{tierLabel}</Tag>
                              {status}
                            </Space>
                          </div>
                          <div style={{ marginTop: 4, fontSize: 12, color: "rgba(0,0,0,0.45)" }}>
                            {formatStorage(record.storageBytes)} · {record.photoCount.toLocaleString()} photos
                          </div>
                        </div>
                      </List.Item>
                    );
                  }}
                />
              ) : (
                <Table
                  dataSource={viewRows}
                  columns={isBeta ? viewColumns.filter((c) => c.key !== "status") : viewColumns}
                  rowKey="viewId"
                  pagination={false}
                  size="small"
                  scroll={{ x: isBeta ? 400 : 700 }}
                />
              )}
            </Card>
          </Col>

          {/* Rendering Credits */}
          {!isBeta && <Col xs={24}>
            <Card title="Rendering Credits">
              <Row gutter={[24, 16]}>
                <Col xs={24} sm={8}>
                  <Statistic
                    title="Balance"
                    value={isBeta ? "Unlimited" : credits.balance.toLocaleString()}
                    prefix={<MaterialIcon name="movie" size={18} />}
                  />
                </Col>
                <Col xs={24} sm={16}>
                  {isBeta ? (
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {credits.consumed.toLocaleString()} credits consumed — unlimited during beta
                    </Text>
                  ) : (
                    <>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        {credits.bonusGranted.toLocaleString()} bonus granted
                        {credits.purchased > 0 && ` + ${credits.purchased.toLocaleString()} purchased`}
                        {" — "}{credits.consumed.toLocaleString()} consumed
                      </Text>

                      <div style={{ marginTop: 12, display: "flex", alignItems: "center", gap: 12 }}>
                        <Switch
                          checked={planV3?.autoPayOverage ?? false}
                          onChange={handleToggleAutoPay}
                          loading={loadingAction === "autopay"}
                          size="small"
                        />
                        <div>
                          <Text style={{ fontSize: 13 }}>Auto-pay overage</Text>
                          <br />
                          <Text type="secondary" style={{ fontSize: 11 }}>
                            €{((plansConfig?.overagePricing?.eurCentsPerThousandCredits ?? 30) / 100).toFixed(2)}/1k credits when pool is empty
                            (min €{((plansConfig?.overagePricing?.minimumChargeEurCents ?? 100) / 100).toFixed(0)} charge)
                          </Text>
                        </div>
                      </div>

                      <div style={{ marginTop: 12 }}>
                        <Space>
                          <InputNumber min={1} max={50} value={addOnCreditsPacks} onChange={(v) => setAddOnCreditsPacks(v || 1)} size="small" style={{ width: 60 }} />
                          <Text type="secondary">× {formatCredits(plansConfig?.addOns?.creditsExtra?.creditsPerPack ?? 10000)}</Text>
                          <Button
                            size="small"
                            onClick={handlePurchaseCredits}
                            loading={loadingAction === "buy-credits"}
                          >
                            Buy €{((plansConfig?.addOns?.creditsExtra?.eurCentsPerPack ?? 300) / 100).toFixed(0)}/pack
                          </Button>
                        </Space>
                      </div>
                    </>
                  )}
                </Col>
              </Row>
            </Card>
          </Col>}
        </Row>
      </div>
    </AppLayout>
  );
}
